import { useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import type { PreviewCaptionState } from "../lib/caption-preview";
import { type PlacementCue } from "../lib/caption-placement";
import {
  scaleStickerByFactor,
  clampStickerRotationMilli,
} from "../lib/sticker-edit";

const CLAMP_X_MILLI = { lo: 80, hi: 920 };
const CLAMP_Y_MILLI = { lo: 90, hi: 910 };

/** Screen-space distance (px) a pointer must travel from the press point
 *  before a pointerdown on the caption body turns into a drag — below this,
 *  releasing is a TAP (no move, no snap). Mirrors the sticker body's
 *  MOVE_THRESHOLD_PX in CreativeOverlay's StickerNode. */
const MOVE_THRESHOLD_PX = 4;

function clampMilli(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, Math.round(v)));
}

/** Pointer position converted to UNCLAMPED per-mille anchor coords, given the
 *  drag frame's bounding rect. Clamping happens after the grab offset is
 *  added back on, same order as the sticker drag helper. */
function pointerAnchorMilliRaw(
  clientX: number,
  clientY: number,
  rect: DOMRect,
): { xMilli: number; yMilli: number } | null {
  if (rect.width <= 0 || rect.height <= 0) return null;
  return {
    xMilli: ((clientX - rect.left) / rect.width) * 1000,
    yMilli: ((clientY - rect.top) / rect.height) * 1000,
  };
}

/**
 * DOM caption overlay over the R3F reel (not drawn into WebGL).
 * When `draggable`, the caption becomes a presentation-only drag handle so the
 * player can reposition it on the video. Position is reported via `onMove` in
 * per-mille anchor units; it never touches the scored edit graph. While the
 * caption tool is active (same `draggable` signal) an inline ‹ prev · name ·
 * next › style switcher renders anchored just above the caption — replaces the
 * old TextTool panel's style chip grid — and a combined resize+rotate handle
 * (mirrors the sticker `creative-sticker-transform` handle) renders at the
 * caption's bottom-left; both are presentation-only (`scaleMilli`/
 * `rotationMilli` never enter the scored edit graph).
 */
export function CaptionOverlay({
  caption,
  draggable = false,
  onMove,
  onDragStart,
  placementCue,
  onCycleStyle,
  onTransform,
}: {
  caption: PreviewCaptionState;
  draggable?: boolean;
  onMove?: (anchorXMilli: number, anchorYMilli: number) => void;
  onDragStart?: () => void;
  /** Cue for the caption's current placement zone (drag-to-place only; no prose rendered). */
  placementCue?: PlacementCue | null;
  /** Cycle the caption STYLE prev (-1) / next (+1) — drives the inline switcher.
   *  Only rendered (and only meaningful) while the caption tool is active. */
  onCycleStyle?: (dir: -1 | 1) => void;
  /** Commit a new scale (milli) + rotation (millidegrees) together — driven by
   *  the bottom-left combined resize+rotate handle. Presentation-only; only
   *  rendered (and only meaningful) while the caption tool is active. */
  onTransform?: (scaleMilli: number, rotationMilli: number) => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    pointerId: number;
    rect: DOMRect;
    /** Press-time anchor minus the pointer's anchor-space position at press —
     *  added back to every subsequent pointer anchor so the caption tracks
     *  the finger by DELTA instead of jumping its center to the press point. */
    grabOffsetXMilli: number;
    grabOffsetYMilli: number;
    startClientX: number;
    startClientY: number;
    /** Flips true once the pointer crosses `MOVE_THRESHOLD_PX`; stays false
     *  for a tap (nothing moves, nothing snaps on release). */
    moving: boolean;
  } | null>(null);
  const transformRef = useRef<{
    pointerId: number;
    rect: DOMRect;
    cx: number;
    cy: number;
    startDist: number;
    startAngle: number;
    scaleMilli: number;
    rotationMilli: number;
  } | null>(null);
  const [liveTransform, setLiveTransform] = useState<{
    scaleMilli: number;
    rotationMilli: number;
  } | null>(null);

  if (!caption.visible) {
    return (
      <div
        className="caption-overlay is-empty"
        data-testid="caption-overlay"
        data-style={caption.style}
        aria-hidden
      />
    );
  }

  const liveScaleMilli = liveTransform?.scaleMilli ?? caption.scaleMilli;
  const liveRotationMilli = liveTransform?.rotationMilli ?? caption.rotationMilli;
  const scale = (liveScaleMilli / 1000) * caption.popScale;
  const rotationDeg = liveRotationMilli / 1000;
  const left = `${caption.anchorXMilli / 10}%`;
  const top = `${caption.anchorYMilli / 10}%`;

  const dragActive = draggable && Boolean(onMove);
  const transformActive = dragActive && Boolean(onTransform);

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragActive) return;
    const parent = e.currentTarget.parentElement;
    if (!parent) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = parent.getBoundingClientRect();
    const pressAnchor = pointerAnchorMilliRaw(e.clientX, e.clientY, rect);
    // Record the grab offset (committed anchor − press point) instead of
    // jumping the caption's center to the press point: a tap must leave it
    // exactly where it was, and a drag must track the finger by delta.
    dragRef.current = {
      pointerId: e.pointerId,
      rect,
      grabOffsetXMilli: pressAnchor ? caption.anchorXMilli - pressAnchor.xMilli : 0,
      grabOffsetYMilli: pressAnchor ? caption.anchorYMilli - pressAnchor.yMilli : 0,
      startClientX: e.clientX,
      startClientY: e.clientY,
      moving: false,
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* pointer capture unavailable (jsdom / synthetic events) — drag still works */
    }
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    e.preventDefault();
    if (!drag.moving) {
      const dx = e.clientX - drag.startClientX;
      const dy = e.clientY - drag.startClientY;
      if (Math.hypot(dx, dy) < MOVE_THRESHOLD_PX) return; // still within tap range
      drag.moving = true;
      onDragStart?.();
    }
    const pointerAnchor = pointerAnchorMilliRaw(e.clientX, e.clientY, drag.rect);
    if (!pointerAnchor || !onMove) return;
    // Track the finger by DELTA: press-time grab offset is added back onto
    // every subsequent pointer anchor so the caption never jumps.
    onMove(
      clampMilli(
        pointerAnchor.xMilli + drag.grabOffsetXMilli,
        CLAMP_X_MILLI.lo,
        CLAMP_X_MILLI.hi,
      ),
      clampMilli(
        pointerAnchor.yMilli + drag.grabOffsetYMilli,
        CLAMP_Y_MILLI.lo,
        CLAMP_Y_MILLI.hi,
      ),
    );
  };

  const endDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    dragRef.current = null;
    try {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    } catch {
      /* no active capture (jsdom / synthetic events) */
    }
    // Free placement: a tap leaves the caption where it was; a drag leaves it
    // exactly where the player dropped it (already reported live via onMove
    // during the move — no snapping to zones). The presentation placement lever
    // still derives its zone from wherever the caption ends up.
  };

  // Combined bottom-left transform handle: distance from the caption's
  // center to the pointer drives the scale factor, angle from the center
  // drives rotation — both previewed live and committed together on release.
  // Mirrors CreativeOverlay's StickerNode `creative-sticker-transform` handle.
  const beginTransform = (e: ReactPointerEvent<HTMLSpanElement>) => {
    if (!onTransform) return;
    e.preventDefault();
    e.stopPropagation();
    const parent = rootRef.current?.parentElement;
    if (!parent) return;
    const rect = parent.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const cx = (caption.anchorXMilli / 1000) * rect.width;
    const cy = (caption.anchorYMilli / 1000) * rect.height;
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const startDist = Math.hypot(px - cx, py - cy) || 1;
    const startAngle = Math.atan2(py - cy, px - cx);
    transformRef.current = {
      pointerId: e.pointerId,
      rect,
      cx,
      cy,
      startDist,
      startAngle,
      scaleMilli: caption.scaleMilli,
      rotationMilli: caption.rotationMilli,
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* not capturable (jsdom / synthetic events) — drag still works */
    }
  };

  const onTransformMove = (e: ReactPointerEvent<HTMLSpanElement>) => {
    const t = transformRef.current;
    if (!t || t.pointerId !== e.pointerId) return;
    e.preventDefault();
    e.stopPropagation();
    const px = e.clientX - t.rect.left;
    const py = e.clientY - t.rect.top;
    const dist = Math.hypot(px - t.cx, py - t.cy) || 1;
    const currentAngle = Math.atan2(py - t.cy, px - t.cx);
    const scaleMilli = scaleStickerByFactor(caption.scaleMilli, dist / t.startDist);
    const deltaDeg = ((currentAngle - t.startAngle) * 180) / Math.PI;
    const rotationMilli = clampStickerRotationMilli(
      caption.rotationMilli + Math.round(deltaDeg * 1000),
    );
    t.scaleMilli = scaleMilli;
    t.rotationMilli = rotationMilli;
    setLiveTransform({ scaleMilli, rotationMilli });
  };

  const endTransform = (e: ReactPointerEvent<HTMLSpanElement>) => {
    const t = transformRef.current;
    if (!t || t.pointerId !== e.pointerId) return;
    e.stopPropagation();
    transformRef.current = null;
    setLiveTransform(null);
    try {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    } catch {
      /* no active capture (jsdom / synthetic events) */
    }
    onTransform?.(t.scaleMilli, t.rotationMilli);
  };

  return (
    <div
      ref={rootRef}
      className={`caption-overlay style-${caption.style}${dragActive ? " is-draggable" : ""}`}
      data-testid="caption-overlay"
      data-style={caption.style}
      data-reveal={caption.revealMode}
      data-draggable={dragActive ? "true" : undefined}
      data-placement-zone={placementCue?.zone}
      title={dragActive ? "Drag to place anywhere" : undefined}
      style={{
        left,
        top,
        transform: `translate(-50%, -50%) scale(${scale}) rotate(${rotationDeg}deg)`,
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      {draggable && onCycleStyle ? (
        <div
          className="caption-style-switcher"
          data-testid="caption-style-switcher"
          // Never start a caption drag from the switcher itself.
          onPointerDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="caption-style-switcher-arrow"
            data-testid="caption-style-prev"
            aria-label="Previous caption style"
            onClick={(e) => {
              e.stopPropagation();
              onCycleStyle(-1);
            }}
          >
            ‹
          </button>
          <button
            type="button"
            className="caption-style-switcher-arrow"
            data-testid="caption-style-next"
            aria-label="Next caption style"
            onClick={(e) => {
              e.stopPropagation();
              onCycleStyle(1);
            }}
          >
            ›
          </button>
        </div>
      ) : null}
      <p className="caption-overlay-text">
        {caption.tokens.map((tok, i) => (
          <span key={`${tok.id}-${i}`} className={tok.emphasized ? "emph" : undefined}>
            {i > 0 ? " " : ""}
            {tok.text}
          </span>
        ))}
      </p>
      {caption.reactionEmoji ? (
        <span
          className="caption-overlay-reaction"
          data-testid="caption-overlay-reaction"
          aria-hidden
        >
          {caption.reactionEmoji}
        </span>
      ) : null}
      {transformActive ? (
        <span
          className="caption-transform"
          data-testid="caption-transform"
          role="slider"
          aria-label="Resize and rotate caption"
          onPointerDown={beginTransform}
          onPointerMove={onTransformMove}
          onPointerUp={endTransform}
          onPointerCancel={endTransform}
        />
      ) : null}
      {/* Placement zone is still tracked (`data-placement-zone` above) from wherever
          the freely-placed caption sits — the zone's explanatory prose ("looks
          official" / "covers context") is intentionally not rendered (R10: no
          on-video prose). */}
    </div>
  );
}
